import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { CartProvider } from "./context/CartContext";
import { ThemeProvider } from "./context/ThemeContext";
import { AdminAuthProvider } from "./context/AdminAuthContext";
import { CustomerAuthProvider } from "./context/CustomerAuthContext";
import { RhmAuthProvider } from "./rhm/RhmAuthContext";
import Account from "./pages/Account";
import AccountAuth from "./pages/AccountAuth";
import GoogleCallback from "./pages/GoogleCallback";
import { GoogleOneTap } from "./components/GoogleOneTap";
import RhmCustomers from "./rhm/pages/RhmCustomers";
import RhmOffers from "./rhm/pages/RhmOffers";
import RhmReorders from "./rhm/pages/RhmReorders";
import RhmDealers from "./rhm/pages/RhmDealers";
import RhmLeads from "./rhm/pages/RhmLeads";
import RhmAutomation from "./rhm/pages/RhmAutomation";
import RhmReports from "./rhm/pages/RhmReports";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CartDrawer from "./components/CartDrawer";
import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ShopCollection from "./pages/ShopCollection";
import ProductDetail from "./pages/ProductDetail";
import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import PaymentReturn from "./pages/PaymentReturn";
import Science from "./pages/Science";
import Consultation from "./pages/Consultation";
import ConsultationBooking from "./pages/ConsultationBooking";
import ConsultationComingSoon from "./pages/ConsultationComingSoon";
import { useConsultEnabled } from "./lib/useConsultEnabled";
import ConsultationIntake from "./pages/ConsultationIntake";
import ConsultationSuccess from "./pages/ConsultationSuccess";
import ConsultationPaymentReturn from "./pages/ConsultationPaymentReturn";
import MyConsultations from "./pages/MyConsultations";
import MyConsultationDetail from "./pages/MyConsultationDetail";
import Admin from "./pages/Admin";
import NotFound from "./pages/NotFound";
import BrandSchema from "./components/BrandSchema";
import SitePage from "./pages/SitePage";
import ManagedRoute from "./pages/ManagedRoute";
import ScienceProduct from "./pages/ScienceProduct";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";
import RhmLayout from "./rhm/RhmLayout";
import Guard from "./rhm/Guard";
import RhmLogin from "./rhm/pages/RhmLogin";
import RhmDashboard from "./rhm/pages/RhmDashboard";
import RhmProducts from "./rhm/pages/RhmProducts";
import RhmProductWorkspace from "./rhm/pages/RhmProductWorkspace";
import RhmQuickProduct from "./rhm/pages/RhmQuickProduct";
import RhmInventory from "./rhm/pages/RhmInventory";
import RhmBatches from "./rhm/pages/RhmBatches";
import RhmSuppliers from "./rhm/pages/RhmSuppliers";
import RhmPurchaseOrders from "./rhm/pages/RhmPurchaseOrders";
import RhmOrders from "./rhm/pages/RhmOrders";
import RhmUsers from "./rhm/pages/RhmUsers";
import RhmRoles from "./rhm/pages/RhmRoles";
import RhmAudit from "./rhm/pages/RhmAudit";
import RhmSettings from "./rhm/pages/RhmSettings";
import RhmDashboardLayout from "./rhm/pages/RhmDashboardLayout";
import RhmConsultations from "./rhm/pages/RhmConsultations";
import RhmBookings from "./rhm/pages/RhmBookings";
import RhmWebsite from "./rhm/pages/RhmWebsite";
import RhmScience from "./rhm/pages/RhmScience";
import RhmSeo from "./rhm/pages/RhmSeo";
import RhmReviews from "./rhm/pages/RhmReviews";
import RhmBlog from "./rhm/pages/RhmBlog";
import RhmBlogEditor from "./rhm/pages/RhmBlogEditor";
import "./App.css";

function PublicShell() {
    return (
        <div className="min-h-screen bg-bg0 text-white flex flex-col">
            <Navbar />
            <main className="flex-1">
                <Outlet />
            </main>
            <Footer />
            <CartDrawer />
        </div>
    );
}

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        if (window.history.scrollRestoration) window.history.scrollRestoration = "manual";
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, [pathname]);
    return null;
}

function AppRoutes() {
    return (
        <>
        <ScrollToTop />
        <BrandSchema />
        <GoogleOneTap />
        <Routes>
                        <Route element={<PublicShell />}>
                            <Route path="/" element={<Home />} />
                            <Route path="/shop" element={<Shop />} />
                            <Route path="/shop/:collectionSlug" element={<ShopCollection />} />
                            <Route path="/product/:slug" element={<ProductDetail />} />
                            <Route path="/checkout" element={<Checkout />} />
                            <Route path="/order/:orderNumber" element={<OrderConfirmation />} />
                            <Route path="/payment/return" element={<PaymentReturn />} />
                            <Route path="/science" element={<ManagedRoute slug="science" fallback={<Science />} />} />
                            <Route path="/science/:productSlug" element={<ScienceProduct />} />
                            <Route path="/consultation" element={<ConsultLanding />} />
                            <Route path="/consultation/book" element={<ConsultGate><ConsultationBooking /></ConsultGate>} />
                            <Route path="/consultation/intake/:bookingId" element={<ConsultGate><ConsultationIntake /></ConsultGate>} />
                            <Route path="/consultation/payment/return" element={<ConsultationPaymentReturn />} />
                            <Route path="/consultation/success/:bookingId" element={<ConsultGate><ConsultationSuccess /></ConsultGate>} />
                            <Route path="/blog" element={<ManagedRoute slug="blog" fallback={<Blog />} />} />
                            <Route path="/blog/category/:categorySlug" element={<Blog />} />
                            <Route path="/blog/:slug" element={<BlogDetail />} />
                            <Route path="/account" element={<Account />} />
                            <Route path="/account/consultations" element={<ConsultGate><MyConsultations /></ConsultGate>} />
                            <Route path="/account/consultations/:bookingId" element={<ConsultGate><MyConsultationDetail /></ConsultGate>} />
                            <Route path="/account/login" element={<AccountAuth mode="login" />} />
                            <Route path="/account/register" element={<AccountAuth mode="register" />} />
                            <Route path="/auth/google" element={<GoogleCallback />} />
                            <Route path="/admin" element={<Admin />} />
                            <Route path="/:slug" element={<SitePage />} />
                            <Route path="*" element={<NotFound />} />
                        </Route>

                        <Route path="/rhm/login" element={<RhmLogin />} />
                        <Route path="/rhm" element={<RhmLayout />}>
                            <Route index element={<Guard perm="dashboard.view"><RhmDashboard /></Guard>} />
                            <Route path="products" element={<Guard perm="products.view"><RhmProducts /></Guard>} />
                            <Route path="products/quick" element={<Guard perm="products.create"><RhmQuickProduct /></Guard>} />
                            <Route path="products/:productId" element={<Guard perm="products.view"><RhmProductWorkspace /></Guard>} />
                            <Route path="inventory" element={<Guard perm="inventory.view"><RhmInventory /></Guard>} />
                            <Route path="batches" element={<Guard perm="batches.view"><RhmBatches /></Guard>} />
                            <Route path="suppliers" element={<Guard perm="suppliers.view"><RhmSuppliers /></Guard>} />
                            <Route path="purchase-orders" element={<Guard perm="purchases.view"><RhmPurchaseOrders /></Guard>} />
                            <Route path="orders" element={<Guard perm="orders.view"><RhmOrders /></Guard>} />
                            <Route path="consultations" element={<Guard perm="consultations.view"><RhmConsultations /></Guard>} />
                            <Route path="bookings" element={<Guard perm="consultations.view"><RhmBookings /></Guard>} />
                            <Route path="website" element={<Guard perm="website.view"><RhmWebsite /></Guard>} />
                            <Route path="science" element={<Guard perm="website.view"><RhmScience /></Guard>} />
                            <Route path="seo" element={<Guard perm="website.view"><RhmSeo /></Guard>} />
                            <Route path="reviews" element={<Guard perm="testimonials.view"><RhmReviews /></Guard>} />
                            <Route path="blog" element={<Guard perm="blog.view"><RhmBlog /></Guard>} />
                            <Route path="blog/:postId" element={<Guard perm="blog.view"><RhmBlogEditor /></Guard>} />
                            <Route path="customers" element={<Guard perm="customers.view"><RhmCustomers /></Guard>} />
                            <Route path="reorders" element={<Guard perm="customers.view"><RhmReorders /></Guard>} />
                            <Route path="offers" element={<Guard perm="offers.view"><RhmOffers /></Guard>} />
                            <Route path="dealers" element={<Guard perm="dealers.view"><RhmDealers /></Guard>} />
                            <Route path="leads" element={<Guard perm="leads.view"><RhmLeads /></Guard>} />
                            <Route path="automation" element={<Guard perm="automation.view"><RhmAutomation /></Guard>} />
                            <Route path="reports" element={<Guard perm="reports.view"><RhmReports /></Guard>} />
                            <Route path="users" element={<Guard perm="users.view"><RhmUsers /></Guard>} />
                            <Route path="roles" element={<Guard perm="roles.view"><RhmRoles /></Guard>} />
                            <Route path="audit" element={<Guard perm="audit.view"><RhmAudit /></Guard>} />
                            <Route path="dashboard-layout" element={<Guard perm="website.view"><RhmDashboardLayout /></Guard>} />
                            <Route path="settings" element={<Guard perm="settings.view"><RhmSettings /></Guard>} />
                        </Route>
        </Routes>
        </>
    );
}

const ConsultGate = ({ children }) => {
    const { mode, loading } = useConsultEnabled();
    if (loading) return null;
    if (mode === "live") return children;
    if (mode === "coming_soon") return <Navigate to="/consultation" replace />;
    return <Navigate to="/" replace />;
};

const ConsultLanding = () => {
    const { mode, loading } = useConsultEnabled();
    if (loading) return null;
    if (mode === "live") return <ManagedRoute slug="consultation" fallback={<Consultation />} />;
    if (mode === "coming_soon") return <ConsultationComingSoon />;
    return <Navigate to="/" replace />;
};

function App() {
    return (
        <BrowserRouter>
            <ThemeProvider>
            <AdminAuthProvider>
                <RhmAuthProvider>
                <CustomerAuthProvider>
                <CartProvider>
                    <AppRoutes />

                    <Toaster
                        position="bottom-right"
                        toastOptions={{
                            style: {
                                background: "rgb(var(--bg1-rgb) / 0.92)",
                                color: "var(--ink)",
                                border: "1px solid var(--line)",
                                borderRadius: "2px",
                                backdropFilter: "blur(20px)",
                                fontFamily: "'JetBrains Mono', monospace",
                                fontSize: "12px",
                            },
                        }}
                    />
                </CartProvider>
                </CustomerAuthProvider>
                </RhmAuthProvider>
            </AdminAuthProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
}

export default App;
